import type { ReactNode } from "react";
import { Navigate, Route, Routes } from "react-router";
import type { Role } from "../shared/types";
import { useCan } from "./context/AuthContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { Shell } from "./components/Shell";
import { CommandProduct } from "./views/cc/CommandCenter";
import { HomeRoute } from "./views/cc/home/HomeRoute";
import { Products } from "./views/Products";
import { NewProduct } from "./views/NewProduct";
import { ProductDetail } from "./views/ProductDetail";
import { ProductProfile } from "./views/products/ProductProfile";
import { Partners } from "./views/Partners";
import { PartnerDetail } from "./views/PartnerDetail";
import { PayRun } from "./views/PayRun";
import { AgreementBuilder } from "./views/AgreementBuilder";
import { Library } from "./views/Library";
import { LibraryItem } from "./views/library/LibraryItem";
import { Lessons } from "./views/library/Lessons";
import { Connections } from "./views/Connections";
import { NewGoal } from "./views/goal/GoalWizard";
import { GetReady } from "./views/ready/GetReady";
import { AudienceStep } from "./views/audience/AudienceStep";
import { AdsStep } from "./views/ads/AdsStep";
import { NeedsView } from "./views/needs/NeedsView";
import { MoreView } from "./views/more/MoreView";

/** A page that only does what `min` may (making a product or a goal, drafting its ads): anyone else goes home. */
function Only({ min, children }: { min: Role; children: ReactNode }) {
  return useCan()(min) ? children : <Navigate to="/command" replace />;
}

export function App() {
  return (
    <Routes>
      <Route element={<ProtectedRoute />}>
        <Route element={<Shell />}>
          <Route index element={<Navigate to="/command" replace />} />
          <Route path="/command" element={<HomeRoute />} />
          <Route path="/command/:productId" element={<CommandProduct />} />
          <Route path="/products" element={<Products />} />
          <Route path="/products/new" element={<Only min="editor"><NewProduct /></Only>} />
          <Route path="/products/:id" element={<ProductDetail />} />
          <Route path="/products/:id/profile" element={<Only min="editor"><ProductProfile /></Only>} />
          <Route path="/products/:id/partners" element={<Only min="admin"><Partners /></Only>} />
          <Route path="/products/:id/payrun" element={<Only min="admin"><PayRun /></Only>} />
          <Route path="/products/:id/agreement" element={<Only min="admin"><AgreementBuilder /></Only>} />
          <Route path="/products/:id/goals/new" element={<Only min="editor"><NewGoal /></Only>} />
          <Route path="/goals/new" element={<Only min="editor"><NewGoal /></Only>} />
          <Route path="/goals/:goalId/ready" element={<Only min="editor"><GetReady /></Only>} />
          <Route path="/goals/:goalId/audience" element={<Only min="editor"><AudienceStep /></Only>} />
          <Route path="/goals/:goalId/ads" element={<Only min="editor"><AdsStep /></Only>} />
          <Route path="/goals/:goalId/*" element={<HomeRoute />} />
          <Route path="/partners/:id" element={<Only min="admin"><PartnerDetail /></Only>} />
          <Route path="/library" element={<Library />} />
          <Route path="/library/lessons" element={<Lessons />} />
          <Route path="/library/:id" element={<LibraryItem />} />
          <Route path="/connections" element={<Connections />} />
          <Route path="/needs" element={<NeedsView />} />
          <Route path="/needs/:id" element={<NeedsView />} />
          <Route path="/more" element={<MoreView />} />
        </Route>
      </Route>
      <Route path="*" element={<Navigate to="/command" replace />} />
    </Routes>
  );
}
